import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface EntitlementModule {
  module_id: string;
  name: string;
  description: string;
  enabled: boolean;
  source: 'owner' | 'workspace_override' | 'org_override' | 'plan' | 'disabled';
}

interface EntitlementSubModule {
  sub_module_id: string;
  name: string;
  module_id: string;
  description: string;
  enabled: boolean;
  source: 'owner' | 'workspace_override' | 'plan' | 'disabled';
}

interface Plan {
  id: string;
  code: string;
  name: string;
}

interface EntitlementsResponse {
  scope: 'workspace' | 'org';
  id: string;
  plan: Plan | null;
  modules: EntitlementModule[];
  sub_modules: EntitlementSubModule[];
  is_owner: boolean;
}

interface ModuleOverride {
  module_id: string;
  enabled: boolean;
}

interface SubModuleOverride {
  sub_module_id: string;
  enabled: boolean;
}

interface UpdateEntitlementsParams {
  action: 'update_plan' | 'set_overrides' | 'set_sub_overrides';
  scope: 'workspace' | 'org';
  scope_id: string;
  plan_code?: string;
  overrides?: ModuleOverride[];
  sub_overrides?: SubModuleOverride[];
}

export function useEntitlements(scope: 'workspace' | 'org', id: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['entitlements', scope, id],
    queryFn: async (): Promise<EntitlementsResponse> => {
      console.log('[useEntitlements] Fetching entitlements:', { scope, id });
      
      const { data, error } = await supabase.functions.invoke('entitlements-get', {
        body: { scope, id }
      });

      if (error) {
        console.error('[useEntitlements] Error:', error);
        throw new Error(error.message || 'Failed to fetch entitlements');
      }

      if (!data) {
        throw new Error('No data received from entitlements API');
      }

      console.log('[useEntitlements] Success:', { 
        scope: data.scope,
        moduleCount: data.modules?.length || 0,
        enabledCount: data.modules?.filter((m: EntitlementModule) => m.enabled).length || 0
      });
      
      return data;
    },
    enabled: !!(scope && id),
    staleTime: 60 * 1000, // 1 minute
    retry: 0 // Prevent retry storms
  });

  const updateMutation = useMutation({
    mutationFn: async (params: UpdateEntitlementsParams) => {
      console.log('[useEntitlements] Updating entitlements:', params);
      
      const { data, error } = await supabase.functions.invoke('entitlements-update', {
        body: params
      });

      if (error) {
        console.error('[useEntitlements] Update error:', error);
        throw new Error(error.message || 'Failed to update entitlements');
      }

      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate both entitlements and legacy workspace-modules queries
      queryClient.invalidateQueries({ queryKey: ['entitlements', variables.scope, variables.scope_id] });
      if (variables.scope === 'workspace') {
        queryClient.invalidateQueries({ queryKey: ['workspace-modules', variables.scope_id] });
      }
      
      toast.success('Entitlements updated successfully');
    },
    onError: (error: Error) => {
      console.error('[useEntitlements] Update error:', error);
      toast.error(error.message || 'Failed to update entitlements');
    },
    retry: 0
  });

  return {
    ...query,
    updateEntitlements: updateMutation.mutate,
    isUpdating: updateMutation.isPending
  };
}

export function useUpdateEntitlements() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateEntitlementsParams) => {
      console.log('[useUpdateEntitlements] Updating:', params);
      
      const { data, error } = await supabase.functions.invoke('entitlements-update', {
        body: params
      });

      if (error) {
        console.error('[useUpdateEntitlements] Error:', error);
        throw new Error(error.message || 'Failed to update entitlements');
      }

      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['entitlements', variables.scope, variables.scope_id] });
      if (variables.scope === 'workspace') {
        queryClient.invalidateQueries({ queryKey: ['workspace-modules', variables.scope_id] });
      }
      
      toast.success('Entitlements updated successfully');
    },
    onError: (error: Error) => {
      console.error('[useUpdateEntitlements] Error:', error);
      toast.error(error.message || 'Failed to update entitlements');
    },
    retry: 0
  });
}